import {debounce} from '@github/mini-throttle'
import {
  type ARIAFilterSuggestion,
  BlockType,
  checkFilterQuerySync,
  type FilterQuery,
  type FilterSuggestion,
  type FilterSuggestionGroup,
  FilterValueType,
  getEscapedFilterValue,
  getFilterBlockChunkByCaret,
  getFilterValue,
  getLastFilterBlockValue,
  getUnescapedFilterValue,
  type IndexedFilterBlock,
  isIndexedFilterBlock,
  MAXIMUM_SUGGESTIONS_TO_SHOW,
  Strings,
} from '@github-ui/filter-core'
import {updateScreenReaderFeedback} from '@github-ui/filter-core/utils/accessibility'
import type {AnchorPosition} from '@primer/behaviors'
import type React from 'react'
import {createContext, startTransition, use, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useDelimiterAnchorPosition} from '../hooks/use-delimiter-anchor-position'
import {useFilterProviders, useFilterQueryDispatch, useFilterQueryState} from './FilterQueryContext'
import {useFilter} from './RootContext'

interface SuggestionsState {
  activeSuggestion: number
  isFetchingSuggestions: boolean
  position?: AnchorPosition
  suggestionGroups: FilterSuggestionGroup[]
  suggestionsVisible?: boolean
}

interface SuggestionsDispatch {
  hideSuggestions: () => void
  showSuggestions: () => void
  suggestionsListRef: React.RefObject<HTMLDivElement | null>
  suggestionSelected: (value: FilterSuggestion) => void
  updateSuggestions: (query: FilterQuery, caret?: number, shouldShowSuggestions?: boolean) => Promise<void>
  activeSuggestionRef: React.RefObject<HTMLLIElement | null>
  setActiveSuggestion: React.Dispatch<React.SetStateAction<number>>
}

export const SuggestionsStateContext = createContext<SuggestionsState | undefined>(undefined)
export const SuggestionsDispatchContext = createContext<SuggestionsDispatch | undefined>(undefined)

export const useSuggestionsState = () => {
  const context = use(SuggestionsStateContext)
  if (!context) {
    throw new Error('useSuggestionsState must be used inside a SuggestionsStateContext')
  }

  return context
}

export const useSuggestionsDispatch = () => {
  const context = use(SuggestionsDispatchContext)
  if (!context) {
    throw new Error('useSuggestionsDispatch must be used inside a SuggestionsDispatchContext')
  }

  return context
}

export {MAXIMUM_SUGGESTIONS_TO_SHOW}

// Module-scope so it keeps a single stable identity for the lifetime of the module. The underlying
// `updateScreenReaderFeedback` is itself a global singleton (it owns a module-level timeout and
// targets the one shared aria-live region), so a shared debounced wrapper is consistent with that
// and coalesces announcements globally. Defining it per render previously made `updateSuggestions`
// — and therefore the suggestions dispatch context and the `updateFromExternal` effect — change
// identity on every keystroke, cascading re-renders, and reset the debounce window each render so
// trailing announcements never coalesced.
const debounceScreenReaderFeedback = debounce(updateScreenReaderFeedback, 500, {start: false})

interface SuggestionsContextProviderProps {
  caretRef: React.RefObject<HTMLSpanElement | null>
  children: React.ReactNode
  inputRef: React.RefObject<HTMLInputElement | null>
}

function excludeUsedSuggestions(suggestionGroup: FilterSuggestionGroup, activeFilterBlock?: IndexedFilterBlock) {
  let suggestions: ARIAFilterSuggestion[] = []
  if (activeFilterBlock) {
    suggestions =
      suggestionGroup.suggestions
        ?.filter(
          suggestion =>
            activeFilterBlock.value.values.findIndex(
              value => getUnescapedFilterValue(value.value) === suggestion.value && !value.hasCaret,
            ) < 0,
        )
        .sort((a, b) => a.priority - b.priority) ?? []
  }
  return {
    ...suggestionGroup,
    suggestions,
  }
}

export const SuggestionsContextProvider = ({children, caretRef, inputRef}: SuggestionsContextProviderProps) => {
  const {config, inputContextRef} = useFilter()
  const {filterQuery, updateFromExternal} = useFilterQueryState()
  const filterProviders = useFilterProviders()
  const {insertIntoQuery, rawFilterRef, replaceActiveBlockWithPresenceBlock, updateFilter} = useFilterQueryDispatch()

  const activeSuggestionRef = useRef<HTMLLIElement | null>(null)
  const suggestionsListRef = useRef<HTMLDivElement>(null)

  const {position, updatePosition} = useDelimiterAnchorPosition({
    caretElementRef: caretRef,
    floatingElementRef: suggestionsListRef,
    inputElementRef: inputRef,
    side: 'outside-bottom',
    align: 'start',
    alignmentOffset: -16,
    allowOutOfBounds: false,
  })

  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false)
  const [suggestionsVisible, setSuggestionsVisible] = useState(false)
  const [suggestionGroups, setSuggestionGroups] = useState<FilterSuggestionGroup[]>([])
  const [activeSuggestion, setActiveSuggestion] = useState<number>(-1)

  // setSuggestionsVisible(true) is idempotent — React bails out of the re-render when the value is
  // unchanged — so no `suggestionsVisible` guard/dependency is needed. Keeping this dependency-free
  // gives showSuggestions a single stable identity for the provider's lifetime; otherwise every
  // show/hide toggle recreated it, which cascaded into a new updateSuggestions, a re-published
  // suggestions dispatch context, and a re-run of the updateFromExternal effect on every toggle.
  const showSuggestions = useCallback(() => {
    setSuggestionsVisible(true)
  }, [])
  const hideSuggestions = useCallback(() => {
    setSuggestionsVisible(false)
    setActiveSuggestion(-1)
  }, [])

  const updateSuggestions = useCallback(
    async (filter: FilterQuery, caret?: number) => {
      let internalShouldShowSuggestions = true
      // If there is no filter input, don't show suggestions
      if (config.variant === 'button') return

      // If the user is currently composing a character (IME support), don't show suggestions
      if (inputContextRef.current?.isComposing) return

      // Track whether we've already measured the popover anchor in this invocation so we can avoid a
      // second getBoundingClientRect (forced reflow) below: position.left depends only on the
      // horizontal delimiter anchor, which is identical for the cached and final passes of the same
      // keystroke (the caret and text are fixed), and the suggestions list consumes only `left`.
      let positionedThisRun = false

      // Clear the list only when it currently shows something. setSuggestionGroups([]) would
      // otherwise allocate a fresh empty array on every keystroke, changing the suggestions state
      // context identity and re-rendering every consumer (Input, SuggestionsList, …) even on
      // keystrokes that never surface a suggestion. Returning the previous reference makes React
      // bail out of the update.
      setSuggestionGroups(prev => (prev.some(group => group.suggestions.length > 0) ? [] : prev))
      // NOTE: We intentionally do NOT measure the popover anchor position here. updatePosition()
      // calls getAnchoredPosition (a getBoundingClientRect read) and runs on every keystroke even
      // when no suggestions end up showing, forcing a synchronous layout that hurts typing INP.
      // Instead we refresh the position right before each showSuggestions() call below, so the
      // layout read only happens when the popover actually appears.

      const query = filter
      const caretIndex = caret ?? inputRef.current?.selectionStart ?? -1

      let cachedSuggestionGroup: FilterSuggestionGroup = {id: 'cached-suggestions', suggestions: []}
      let cachedWithoutUsedSuggestionGroup = cachedSuggestionGroup
      let aggregatedSuggestionGroups: FilterSuggestionGroup[] = []
      const activeBlock = query.activeBlock
      const activeFilterBlock = activeBlock && isIndexedFilterBlock(activeBlock) ? activeBlock : undefined

      const queryValue = activeBlock ? getLastFilterBlockValue(activeBlock, caretIndex) : ''

      const isCompleteResultSetQueryOrTextBlockOrOtherTypeOfNonSpecificBlock =
        queryValue && activeFilterBlock && activeFilterBlock.provider?.isCompleteResultSetQuery?.(queryValue)

      // Before fetching async, we will check the provider for any pre-fetched suggestions that match the current filter value
      if (activeFilterBlock) {
        const [type] = getFilterBlockChunkByCaret(activeFilterBlock, caretIndex)

        // Look for cached suggestions if the caret is positioned on the value, and only if it even has a value
        if (type === 'value' && queryValue) {
          cachedSuggestionGroup = {
            id: 'cached-suggestions',
            suggestions: activeFilterBlock.provider?.findPrefetchedSuggestions?.(queryValue) ?? [],
          }
          cachedWithoutUsedSuggestionGroup = excludeUsedSuggestions(cachedSuggestionGroup, activeFilterBlock)

          if (cachedWithoutUsedSuggestionGroup && cachedWithoutUsedSuggestionGroup.suggestions.length > 0) {
            // Refresh the anchor position immediately before showing so the popover renders in the
            // right place; batched with showSuggestions() into a single render.
            updatePosition()
            positionedThisRun = true
            // Defer the popup-opening commits (visibility + groups) into a transition so the typed
            // character paints first. Opening the popup the first time mounts SuggestionsList +
            // the popover, which is the dominant per-interaction cost on the first keystroke; as a
            // non-urgent transition React commits the urgent input update (the typed character)
            // first and renders the popup in a follow-up render, keeping keystroke INP low. The
            // suggestion still appears on the very next frame, so perceived latency is unchanged.
            startTransition(() => {
              // Make sure the menu is visible to prevent it from flashing
              showSuggestions()
              // Immediately put any cached suggestions into the suggestions list while we wait for
              // any additional suggestions to come async so the user sees immediate feedback.
              setSuggestionGroups([cachedWithoutUsedSuggestionGroup])
            })

            debounceScreenReaderFeedback(
              `${cachedWithoutUsedSuggestionGroup.suggestions.length} ${
                cachedWithoutUsedSuggestionGroup.suggestions.length === 1 ? 'suggestion' : 'suggestions'
              }`,
            )
          }
        }
      }

      // We only want to fetch async suggestions if there aren't enough cached suggestions to fill the list.
      // This should always be the case if prefetching is disabled, as the cachedSuggestions array is empty.
      if (
        cachedWithoutUsedSuggestionGroup.suggestions.length < MAXIMUM_SUGGESTIONS_TO_SHOW &&
        !isCompleteResultSetQueryOrTextBlockOrOtherTypeOfNonSpecificBlock
      ) {
        // Notify state we are beginning to fetch
        if (activeFilterBlock && activeFilterBlock.provider?.isCompleteResultSetQuery?.(queryValue) !== undefined) {
          setIsFetchingSuggestions(true)
        }

        aggregatedSuggestionGroups = await query.getSuggestions(caretIndex, filterProviders, config)

        // Keep the cached suggestions at the front of the list to prevent the results from shifting away from the user.
        aggregatedSuggestionGroups[0]?.suggestions.unshift(...cachedSuggestionGroup.suggestions)

        // Remove any duplicates
        const seen = new Set<string>()
        if (aggregatedSuggestionGroups[0]) {
          aggregatedSuggestionGroups[0].suggestions = aggregatedSuggestionGroups[0]?.suggestions.filter(suggestion => {
            const key = getFilterValue(suggestion.value) ?? ''
            return seen.has(key) ? false : seen.add(key)
          })
        }

        if (aggregatedSuggestionGroups[0] && activeBlock?.type === BlockType.Text && activeBlock.raw.startsWith('-')) {
          aggregatedSuggestionGroups[0].title = Strings.exclude
        }
      } else {
        // If we have enough cached suggestions to fill the list, just use those
        aggregatedSuggestionGroups = [cachedSuggestionGroup]
      }

      if (activeFilterBlock) {
        // Hide suggestion if the first suggestion is the currently entered filter value
        const indexedValue = getLastFilterBlockValue(activeFilterBlock, caretIndex)
        const firstGroupsSuggestions = aggregatedSuggestionGroups[0]?.suggestions
        if (
          aggregatedSuggestionGroups.length === 1 &&
          firstGroupsSuggestions &&
          getFilterValue(firstGroupsSuggestions[0]?.value) === indexedValue &&
          indexedValue.length > 0
        ) {
          // If there are no more suggestions
          if (firstGroupsSuggestions.length === 1) {
            internalShouldShowSuggestions = false
          }
        }
      }

      if (activeFilterBlock && aggregatedSuggestionGroups[0]) {
        // Exclude any values that are already in the filter
        aggregatedSuggestionGroups[0] = excludeUsedSuggestions(aggregatedSuggestionGroups[0], activeFilterBlock)
        // Limit the number of suggestions to show
        aggregatedSuggestionGroups[0].suggestions = aggregatedSuggestionGroups[0].suggestions.slice(
          0,
          MAXIMUM_SUGGESTIONS_TO_SHOW,
        )
      }

      if (checkFilterQuerySync(query, rawFilterRef?.current) && query.isValidated) {
        // Final check to make sure the query hasn't changed while we were fetching suggestions and that is validated
        const suggestionCounts = aggregatedSuggestionGroups.reduce((n, group) => n + group.suggestions.length, 0)

        // Ensure we only announce available suggestions if the user is focused on the input and we have suggestions to show
        if (document.activeElement === inputRef.current && suggestionCounts > 0) {
          debounceScreenReaderFeedback(
            `${suggestionCounts} ${suggestionCounts === 1 ? 'suggestion' : 'suggestions'}`,
            false,
          )
        }
        // Avoid reallocating an empty list: keep the previous reference (React bails out, no
        // re-render) when neither the current nor the next groups contain any suggestions — the
        // common case for keystrokes that never show suggestions.
        //
        // Defer the popup-opening commits (groups + visibility) into a transition so the urgent
        // input update (the typed character) paints first and the popup mount/measure happens in a
        // follow-up render — keeping keystroke INP low without changing perceived latency.
        startTransition(() => {
          setSuggestionGroups(prev =>
            suggestionCounts > 0 || prev.some(group => group.suggestions.length > 0)
              ? aggregatedSuggestionGroups
              : prev,
          )
          // Reset the active suggestion if we replace the entire contents of the list
          if (cachedSuggestionGroup.suggestions.length === 0) setActiveSuggestion(-1)

          if (
            aggregatedSuggestionGroups.length > 0 &&
            internalShouldShowSuggestions &&
            inputContextRef.current?.inputHasFocus
          ) {
            // Refresh the anchor position immediately before showing so the popover renders in the
            // right place; batched with showSuggestions() into a single render. Skip when the cached
            // pass above already measured this run — position.left is unchanged, so re-measuring would
            // only add a redundant forced reflow.
            if (!positionedThisRun) updatePosition()
            showSuggestions()
          } else {
            hideSuggestions()
          }
        })
      }

      setIsFetchingSuggestions(false)
    },
    [
      config,
      inputRef,
      filterProviders,
      showSuggestions,
      updatePosition,
      rawFilterRef,
      inputContextRef,
      hideSuggestions,
    ],
  )

  // Keep the latest filter-query actions (and `filterQuery`) in a ref so `suggestionSelected` can
  // stay referentially stable across keystrokes. These actions and `filterQuery.activeBlock` all
  // change identity on every keystroke; closing over them directly would republish the suggestions
  // dispatch context each keystroke and re-render every consumer (e.g. every `SuggestionItem`).
  // `suggestionSelected` only runs on user selection \u2014 well after commit \u2014 so reading the latest
  // values from a ref synced in an effect is safe and behaviourally identical.
  const suggestionSelectActionsRef = useRef({
    filterQuery,
    insertIntoQuery,
    replaceActiveBlockWithPresenceBlock,
    updateFilter,
  })
  useEffect(() => {
    suggestionSelectActionsRef.current = {
      filterQuery,
      insertIntoQuery,
      replaceActiveBlockWithPresenceBlock,
      updateFilter,
    }
  }, [filterQuery, insertIntoQuery, replaceActiveBlockWithPresenceBlock, updateFilter])

  const suggestionSelected = useCallback(
    (suggestion: FilterSuggestion) => {
      const {
        filterQuery: currentQuery,
        insertIntoQuery: insertIntoQueryAction,
        replaceActiveBlockWithPresenceBlock: replaceActiveBlockWithPresenceBlockAction,
        updateFilter: updateFilterAction,
      } = suggestionSelectActionsRef.current

      setActiveSuggestion(-1)
      const currentBlock = currentQuery.activeBlock
      const isExcludeKeySuggestion = suggestion.value.toString().startsWith('-')

      let suggestedValue
      let shouldSubmit = false

      // If the user selects the Divider, we abort early and do nothing
      if (suggestion.value === Strings.dividerValue) return

      // Selected Filter Key
      if (suggestion.type === 'keyword') {
        suggestedValue = getEscapedFilterValue(suggestion.value)
      } else if (!currentBlock || currentBlock.type !== BlockType.Filter) {
        suggestedValue = getEscapedFilterValue(suggestion.value)

        // only add the colon if the suggestion value is NOT the exclude key and doesn't end with a dot,
        // indicating a nested filter key
        if (suggestedValue !== '-' && !suggestedValue?.endsWith('.')) {
          suggestedValue += config.filterDelimiter
        }
        // Selected Filter Value
      } else if (isExcludeKeySuggestion) {
        suggestedValue = getEscapedFilterValue(suggestion.value)
      } else {
        suggestedValue = getEscapedFilterValue(suggestion.value)
        shouldSubmit = true
        hideSuggestions()
      }

      const caretIndex = inputRef.current?.selectionStart ?? -1

      if (suggestion.type === FilterValueType.NoValue) {
        replaceActiveBlockWithPresenceBlockAction('no')
      } else if (suggestion.type === FilterValueType.HasValue) {
        replaceActiveBlockWithPresenceBlockAction('has')
      } else if (suggestedValue) {
        insertIntoQueryAction(suggestedValue, caretIndex, shouldSubmit)
      } else {
        updateFilterAction()
      }

      // Previously done by `SuggestionItem` after calling `suggestionSelected`; folded in here so
      // the item no longer needs to consume the (per-keystroke) input dispatch context for `inputRef`.
      inputRef.current?.focus()
    },
    [config.filterDelimiter, hideSuggestions, inputRef, setActiveSuggestion],
  )

  // Trigger a suggestions refresh when the raw query text changes. We intentionally key on
  // filterQuery.raw (a string) and NOT the filterQuery object: the validation pass commits a fresh
  // FilterQuery object with the same raw text (it only adds error styling), and updateFromExternal
  // short-circuits on those (its rawFilter guard is unchanged), so re-running this effect for them
  // is pure waste. Dropping the object dep also removes the effect->setState->effect re-arming that
  // an object-identity trigger would expose if this commit were ever deferred.
  useEffect(() => {
    updateFromExternal(async query => {
      if (checkFilterQuerySync(query, rawFilterRef?.current)) {
        await updateSuggestions(query, inputRef.current?.selectionStart ?? -1)
      }
    })
  }, [rawFilterRef, updateSuggestions, filterQuery.raw, updateFromExternal, inputRef])

  const suggestionsStateValue = useMemo<SuggestionsState>(
    () => ({
      activeSuggestion,
      isFetchingSuggestions,
      position,
      suggestionGroups,
      suggestionsVisible,
    }),
    [activeSuggestion, isFetchingSuggestions, position, suggestionGroups, suggestionsVisible],
  )

  const suggestionsDispatchValue = useMemo<SuggestionsDispatch>(
    () => ({
      hideSuggestions,
      showSuggestions,
      suggestionsListRef,
      suggestionSelected,
      updateSuggestions,
      activeSuggestionRef,
      setActiveSuggestion,
    }),
    [activeSuggestionRef, hideSuggestions, showSuggestions, suggestionSelected, updateSuggestions],
  )

  return (
    <SuggestionsStateContext value={suggestionsStateValue}>
      <SuggestionsDispatchContext value={suggestionsDispatchValue}>{children}</SuggestionsDispatchContext>
    </SuggestionsStateContext>
  )
}
